import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
  statusCode?: number;
}

export interface ApiError {
  message: string;
  statusCode?: number;
  errors?: any;
}

class HttpClient {
  private instance: AxiosInstance;

  constructor(baseURL: string) {
    this.instance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.initializeRequestInterceptor();
    this.initializeResponseInterceptor();
  }

  private initializeRequestInterceptor() {
    this.instance.interceptors.request.use(
      (config) => {
        // Add authentication token if available
        const token = localStorage.getItem("authToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  private initializeResponseInterceptor() {
    this.instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        return this.handleError(error);
      }
    );
  }

  private handleError(error: AxiosError): Promise<ApiError> {
    const apiError: ApiError = {
      message: "An unexpected error occurred",
      statusCode: error.response?.status,
    };

    if (error.response) {
      // Server responded with error
      const data = error.response.data as any;
      apiError.message = data?.message || error.message;
      apiError.errors = data?.errors;
      apiError.statusCode = error.response.status;

      // Handle specific status codes
      if (error.response.status === 401) {
        // Unauthorized - the session is dead server-side (expired or
        // invalidated by a password change), so clear it fully rather than
        // just the token, which otherwise leaves stale cached user state.
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
        console.warn("Unauthorized access - token may be invalid");
      } else if (error.response.status === 403) {
        apiError.message = "Access forbidden";
      } else if (error.response.status === 404) {
        apiError.message = "Resource not found";
      } else if (error.response.status === 500) {
        apiError.message = "Internal server error";
      }
    } else if (error.request) {
      // Request made but no response
      apiError.message =
        "No response from server. Please check your internet connection.";
    } else {
      // Error in request setup
      apiError.message = error.message;
    }

    return Promise.reject(apiError);
  }

  // GET request
  public async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.get(url, config);
    return response.data;
  }

  // POST request
  public async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.post(
      url,
      data,
      config
    );
    return response.data;
  }

  // PUT request
  public async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.put(
      url,
      data,
      config
    );
    return response.data;
  }

  // PATCH request
  public async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.patch(
      url,
      data,
      config
    );
    return response.data;
  }

  // DELETE request
  public async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.delete(url, config);
    return response.data;
  }

  // Get axios instance for custom requests
  public getAxiosInstance(): AxiosInstance {
    return this.instance;
  }
}

export default HttpClient;
